/** @module csi/datasheet/Note */
define([
	"dojo/_base/declare",
	"dijit/_TemplatedMixin",
	"dijit/_WidgetBase",
	"canvas/widgets/interfaces/model/enumerations/CanvasMode",
	"csi/html",
	"csi/types",
	"csi/widget/froalaTextEditor",
	"dojo/text!./Note.html"
], function(declare, _TemplatedMixin, _WidgetBase, { CanvasMode }, html, types, froalaTextEditor, template) {

	const _Note = declare([_WidgetBase, _TemplatedMixin], {
		baseClass: "csi-canvas-note",
		templateString: template,

		// extension points
		authorNode: null,
		dateNode: null,
		deleteNode: null,
		textNode: null,

		/** @property {string} author */
		author: "",

		/** @property {string} authorOnEdit - author to use while editing, if different from author */
		authorOnEdit: "",

		/** @property {Object} canvasApp - canvas instance */
		canvasApp: null,

		/** @property {string} lastValue */
		lastValue: null,

		/** @property {string} text */
		text: "",

		/** @property {number} time */
		time: "",

		/** @property {string} timeFormat - format to use to render time */
		timeFormat: types.FORMAT_DIFF,

		/** @property {froalaTextEditor} editor */
		_editor: null,

		/**
		 * Extension point for calling onChange manually
		 * @param {Note} item
		 */
		onChangeNote: function(item) { },

		/**
		 * Focus on text editor
		 */
		focus: function() {
			if (this._editor) {
				this._editor.focus();
			}
		},

		/**
		 * Render text on startup once disabled status is known
		 */
		startup: function() {
			if (!this._started) {
				this.inherited(arguments);
				this._render();
			}
		},

		/**
		 * Blur event for note.
		 * Note: ( C8-62902 ) Blur event given under Froala's standard options does not fire when CMD/CTRL is pressed as the last button.
		 * Fix: Call this function, if exists, in toggleRefNodeSelection, as a final save to the note before switching pages.
		 */
		handleBlur() {
			if (!this._editor) {
				// if editor is not initialized, then remove blur callback and do nothing
				this.view.blurCurrentNote = null;
				return;
			}

			const { 
				_editor: { froalaEditor }, 
				author, 
				authorOnEdit, 
				baseClass, 
				canvasApp: { pageHelper }, 
				domNode, 
				view 
			} = this;
			const currentValue = froalaEditor.html.get();

			view.blurCurrentNote = null;
			froalaEditor.toolbar.hide();

			domNode.classList.toggle(baseClass + "-active", false);
			pageHelper.expandEditor(pageHelper.getEditorDimensions(), false, true);

			if (currentValue !== this.lastValue) {
				// change text, update timestamp, and set author to current user
				this.set("author", authorOnEdit || author);
				this.set("time", types.getDateTime());
				this._set("text", currentValue);
				this.onChange(currentValue);
				this.lastValue = currentValue;
			}
		},

		/**
		 * Render text either as simple HTML, or using editor when in edit mode
		 */
		_render: function() {
			const { baseClass, canvasApp, domNode, textNode, view } = this;
			if (this._started) {
				domNode.classList.toggle(baseClass + "-active", false);
				html.destroyChildren(textNode, true);
				
				if (this.disabled) {
					this._editor = null;
					html.richText(this.text, textNode);
					textNode.classList.add(html.CSS_RICH_TEXT);
				} else {
					textNode.classList.remove(html.CSS_RICH_TEXT);

					this._editor = new froalaTextEditor({
						disabled: false,
						inlineEditor: true,
						value: this.text,
						onFocus: () => {
							// deselect all objects when notes are focused
							canvasApp.selectionApi().deselectAllObjects();
							domNode.classList.toggle(baseClass + "-active", true);

							// set canvas mode to EDIT_TEXT
							canvasApp.canvasApi().setCanvasMode(CanvasMode.EDIT_TEXT);

							// set callback to handle blur event
							view.blurCurrentNote = (e) => this.handleBlur(e);
						},
						onBlur: (e) => this.handleBlur(e),
					}, textNode);
				}
			}
		},

		/**
		 * Render note author
		 * @param {string} value
		 */
		_setAuthorAttr: function(value) {
			this._set("author", value);
			html.clear(this.authorNode);
			html.text(this.authorNode, value);
		},

		/**
		 * Update disabled status and rendering
		 * @param {boolean} value
		 */
		_setDisabledAttr: function(value) {
			this._set("disabled", value);
			this._render();
		},

		/**
		 * Render note text
		 * @param {string} value
		 */
		_setTextAttr: function(value) {
			this._set("text", value);
			this._render();
		},

		/**
		 * Render note time
		 * @param {string} value
		 */
		_setTimeAttr: function(value) {
			this._set("time", value);
			html.clear(this.timeNode);
			html.text(this.timeNode, types.formatTime(value, this.timeFormat));
		}
	});

	// specify properties of widget that can be used for csi/widget/Container
	_Note.widgetProperties = ["author", "authorOnEdit", "text", "time"];

	return _Note;
});
